import { z } from 'zod';
import { ScenarioStepInputSchema, ScenarioStepSchema } from './scenarios';

export const STEP_COMPONENT_STATUS = ['active', 'archived'] as const;
export type StepComponentStatus = (typeof STEP_COMPONENT_STATUS)[number];

export const QualityStepComponentSchema = z.object({
  id: z.string().uuid(),
  key: z.string().min(1).max(80),
  name: z.string().min(1).max(200),
  description: z.string().max(2_000).default(''),
  status: z.enum(STEP_COMPONENT_STATUS),
  version: z.number().int().positive(),
  steps: z.array(ScenarioStepSchema),
  /** Declared parameter names used in step test data placeholders (${name}). */
  parameters: z.array(z.string().max(80)).default([]),
  stepCount: z.number().int().nonnegative().optional(),
  usageCount: z.number().int().nonnegative().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdByName: z.string().nullable().optional(),
  updatedByName: z.string().nullable().optional(),
});
export type QualityStepComponent = z.infer<typeof QualityStepComponentSchema>;

export const StepComponentListResponseSchema = z.object({
  projectId: z.string().uuid(),
  components: z.array(QualityStepComponentSchema),
});
export type StepComponentListResponse = z.infer<typeof StepComponentListResponseSchema>;

export const StepComponentResponseSchema = z.object({
  component: QualityStepComponentSchema,
});
export type StepComponentResponse = z.infer<typeof StepComponentResponseSchema>;

export const CreateStepComponentRequestSchema = z.object({
  key: z.string().trim().min(1).max(80),
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2_000).optional(),
  steps: z.array(ScenarioStepInputSchema).max(50).optional(),
  parameters: z.array(z.string().trim().min(1).max(80)).max(20).optional(),
});
export type CreateStepComponentRequest = z.infer<typeof CreateStepComponentRequestSchema>;

export const PatchStepComponentRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(2_000).optional(),
  status: z.enum(STEP_COMPONENT_STATUS).optional(),
  steps: z.array(ScenarioStepInputSchema).max(50).optional(),
  parameters: z.array(z.string().trim().min(1).max(80)).max(20).optional(),
});
export type PatchStepComponentRequest = z.infer<typeof PatchStepComponentRequestSchema>;

export const StepComponentLinkedScenarioSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  title: z.string(),
  status: z.string(),
});
export type StepComponentLinkedScenario = z.infer<typeof StepComponentLinkedScenarioSchema>;

export const StepComponentScenariosResponseSchema = z.object({
  componentId: z.string().uuid(),
  scenarios: z.array(StepComponentLinkedScenarioSchema),
  usageCount: z.number().int().nonnegative(),
});
export type StepComponentScenariosResponse = z.infer<typeof StepComponentScenariosResponseSchema>;

/** Extract `${param}` placeholders from component step test data. */
export function extractComponentParameterNames(steps: Array<{ testData?: string; inputData?: string }>): string[] {
  const names = new Set<string>();
  const pattern = /\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
  for (const step of steps) {
    const text = `${step.testData ?? ''}\n${step.inputData ?? ''}`;
    for (const match of text.matchAll(pattern)) {
      const name = match[1];
      if (name) names.add(name);
    }
  }
  return [...names].sort();
}
